/**
 * @module Hooks.UsePromoOffer
 */
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { resolvePromoOffer } from "app/actions/promoOffer";
import { PromoOffer } from "app/models";
import { Currency } from "app/utils/currency";

interface UsePromoOfferParams {
  code?: string;
  planCode: string;
  currency: Currency;
  // Wait for auth before resolving — a tokenless resolve 401s (and noisily
  // retries) on the signup→onboarding hop, since the endpoint requires auth.
  ready?: boolean;
}

interface UsePromoOfferResult {
  offer: PromoOffer | null;
  baseAmount: number | undefined;
  // The resolved trial length for the current (code, plan) — backend-sourced and
  // region-aware, so pages don't re-derive the regional trial client-side.
  trialDays: number | undefined;
  loading: boolean;
  error: boolean;
  // The plan a code term-locks to (or null) — discovered on a CODE-only match so
  // a page can pin to it; see the return note below.
  lockedPlan: string | null;
  resolveOffer: () => void;
}

/**
 * Resolves the promo offer for a checkout page's `(code, plan, currency)` and
 * exposes the derived display state, so the checkout routes don't each
 * re-implement the resolve dispatch + freshness / loading / error / retry logic.
 *
 * `loading` and the trusted `offer` / `baseAmount` are gated on the resolution
 * echoing BOTH the current code and plan — a term toggle keeps the code but
 * changes the plan, so a code-only check would surface the previous term's data.
 */
export function usePromoOffer({
  code,
  planCode,
  currency,
  ready = true,
}: UsePromoOfferParams): UsePromoOfferResult {
  const dispatch = useDispatch();
  const resolution = useSelector((state: RootState) => state.promoOffer);

  const resolveOffer = useCallback(() => {
    dispatch(resolvePromoOffer({ code: code || "", planCode, currency }));
  }, [dispatch, code, planCode, currency]);

  useEffect(() => {
    if (!ready) {
      return;
    }
    resolveOffer();
  }, [resolveOffer, ready]);

  const codeMatches =
    (resolution.code || "").toUpperCase() === (code || "").toUpperCase();
  // Currency too: the slice is shared across routes, so a resolution for the same
  // (code, plan) in another currency must not be trusted as this page's price.
  const isCurrent =
    codeMatches && resolution.planCode === planCode && resolution.currency === currency;

  return {
    offer: isCurrent ? resolution.offer : null,
    baseAmount: isCurrent ? resolution.baseAmount ?? undefined : undefined,
    trialDays: isCurrent ? resolution.trialDays ?? undefined : undefined,
    loading: resolution.pending || !isCurrent,
    error: Boolean(resolution.error) && isCurrent,
    // Term-lock discovery is CODE-only: the backend resolves the offer for the
    // EFFECTIVE (locked) plan, whose planCode differs from the requested one, so
    // the strict (code+plan) gate would hide the lock and the page could never
    // pin to it. offer/baseAmount/loading stay strict — the lock is a hint.
    lockedPlan: codeMatches ? resolution.offer?.planLock ?? null : null,
    resolveOffer,
  };
}
